// components/tracker/digest-next/grain-shader.ts
//
// A shared, React-free mount for Paper's GrainGradient (FE-DIGEST-1 Phase
// D3b2, round 2). It generalises the sequence the hero uses privately
// (`digest-hero.tsx`, which moves onto this module in D3c with its own
// re-check):
//
//   1. a WebGL2 probe whose context is released at once, so a device without
//      WebGL2 never reaches the renderer and keeps its still image;
//   2. waiting for the noise texture's `load` event, not `decode()`, which
//      can stall while a tab is hidden (ShaderMount throws on an unloaded
//      image);
//   3. the vanilla ShaderMount (not the React wrapper, which initialises
//      asynchronously with no rejection handler) at a pixel budget and a
//      minimum pixel ratio;
//   4. a `webglcontextlost` listener in the capture phase;
//   5. a release that disposes, loses the context and removes the canvas.
//
// Only ShaderMount's public API is used: setUniforms, dispose,
// canvasElement. Its private render and gl are not touched.

import {
  ShaderMount,
  grainGradientFragmentShader,
  getShaderColorFromString,
  getShaderNoiseTexture,
  GrainGradientShapes,
  ShaderFitOptions,
  defaultObjectSizing,
  type GrainGradientShape,
} from "@paper-design/shaders"

export type GrainParams = {
  colors: readonly string[]
  colorBack: string
  softness: number
  intensity: number
  noise: number
  shape: GrainGradientShape
  scale: number
  rotation: number
  offsetX: number
  offsetY: number
}

/** The uniforms for a palette and the shared parameters, colour count
 *  included (a four-colour palette after a five-colour one must say so). */
export function grainUniforms(p: GrainParams) {
  const sizing = defaultObjectSizing
  return {
    u_colorBack: getShaderColorFromString(p.colorBack),
    u_colors: p.colors.map((c) => getShaderColorFromString(c)),
    u_colorsCount: p.colors.length,
    u_softness: p.softness,
    u_intensity: p.intensity,
    u_noise: p.noise,
    u_shape: GrainGradientShapes[p.shape],
    u_fit: ShaderFitOptions[sizing.fit],
    u_scale: p.scale,
    u_rotation: p.rotation,
    u_offsetX: p.offsetX,
    u_offsetY: p.offsetY,
    u_originX: sizing.originX,
    u_originY: sizing.originY,
    u_worldWidth: sizing.worldWidth,
    u_worldHeight: sizing.worldHeight,
  }
}

/** True when a WebGL2 context can be made. The probe's context is released
 *  at once, so it does not count against the page's live contexts. */
export function probeWebGL2(): boolean {
  try {
    const probe = document.createElement("canvas").getContext("webgl2")
    if (!probe) return false
    probe.getExtension("WEBGL_lose_context")?.loseContext()
    return true
  } catch {
    return false
  }
}

/** Loses a canvas's WebGL2 context and removes it from the page. */
export function loseCanvas(canvas: HTMLCanvasElement | null | undefined) {
  canvas?.getContext("webgl2")?.getExtension("WEBGL_lose_context")?.loseContext()
  canvas?.remove()
}

export type GrainMount = {
  /** The live renderer, once started. */
  readonly mount: ShaderMount | null
  /** Change uniforms in place (palette, colour count). Redraws at once. */
  setUniforms: (params: GrainParams) => void
  /** Dispose, lose the context and remove the canvas. Idempotent. */
  release: () => void
}

/**
 * Mounts a static GrainGradient into `host` when WebGL2 is available and the
 * noise texture has loaded. `onReady` fires once the renderer has drawn
 * (two frames after the mount), `onFail` when it cannot start, and `onLost`
 * when the context is lost later. The caller keeps its still image beneath
 * until `onReady`.
 */
export function mountGrainGradient(
  host: HTMLElement,
  params: GrainParams,
  options: {
    frame: number
    speed?: number
    minPixelRatio: number
    maxPixelCount: number
    /** For a still copy of the canvas taken with drawImage. */
    preserveDrawingBuffer?: boolean
    onReady: () => void
    onFail: () => void
    onLost: () => void
  },
): GrainMount {
  let mount: ShaderMount | null = null
  let released = false
  let raf = 0
  let texture: HTMLImageElement | undefined

  const release = () => {
    if (released) return
    released = true
    cancelAnimationFrame(raf)
    texture?.removeEventListener("load", onLoad)
    host.removeEventListener("webglcontextlost", lost, true)
    const canvas = mount?.canvasElement
    mount?.dispose()
    mount = null
    loseCanvas(canvas)
  }

  function lost() {
    if (released) return
    release()
    options.onLost()
  }

  function start(img: HTMLImageElement) {
    if (released) return
    try {
      mount = new ShaderMount(
        host,
        grainGradientFragmentShader,
        { ...grainUniforms(params), u_noiseTexture: img },
        options.preserveDrawingBuffer ? { preserveDrawingBuffer: true } : undefined,
        options.speed ?? 0,
        options.frame,
        options.minPixelRatio,
        options.maxPixelCount,
      )
    } catch {
      // The constructor appends its canvas before asking for a context.
      host.querySelectorAll("canvas").forEach((c) => loseCanvas(c))
      mount = null
      options.onFail()
      return
    }
    // Let the renderer's ResizeObserver and first static frame run.
    raf = requestAnimationFrame(() => {
      raf = requestAnimationFrame(() => {
        const gl = mount?.canvasElement.getContext("webgl2")
        if (!released && mount && gl && !gl.isContextLost()) options.onReady()
      })
    })
  }

  function onLoad() {
    if (texture) start(texture)
  }

  host.addEventListener("webglcontextlost", lost, true)

  if (!probeWebGL2()) {
    options.onFail()
  } else {
    texture = getShaderNoiseTexture()
    if (!texture) options.onFail()
    else if (texture.complete && texture.naturalWidth > 0) start(texture)
    else texture.addEventListener("load", onLoad, { once: true })
  }

  return {
    get mount() {
      return mount
    },
    setUniforms(next: GrainParams) {
      mount?.setUniforms(grainUniforms(next))
    },
    release,
  }
}
